import { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

import { useTheme } from '../../Context/ThemeContext';
import { useLanguage } from '../../Context/LanguageContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import { ReactComponent as LeftArrowSvg } from '../../image/LeftArrow.svg';
import WhiteTrashIcon from '../../image/WhiteTrashIcon.png';

export default function PaymentOptionSettingSelectBox(props) {
  const {
    index,
    value,
    setValue,
    options,
    disabled,
    width,
    includeLinkoPayOption,
    onClickAdditionOption,
    onClickDeleteOption
  } = props;

  const displayOptions = includeLinkoPayOption
    ? [{ id: 0, name: '링코페이' }, ...options]
    : options;

  const { isDark } = useTheme();
  const { t } = useLanguage();
  const theme = isDark ? darkTheme : lightTheme;

  const PAYMENT_NAME_MAP = {
    '현금': t('paymentSelectBox.cash'),
    '카드': t('paymentSelectBox.card'),
    '포인트': t('paymentSelectBox.point'),
    '링코페이': t('paymentSelectBox.linkoPay'),
  };

  function getDisplayName(name) {
    return PAYMENT_NAME_MAP[name] ?? name;
  }

  const [isClicked, setIsClicked] = useState(false);
  const [isAdditionButtonHover, setIsAddionButtonHover] = useState(false);

  const TopLevelRef = useRef();
  const SelectRef = useRef();
  const OptionRef = useRef([]);
  const OptionTextRef = useRef([]);


  // 컴포넌트 외부 클릭시 이벤트를 받기위한 리스너 연결
  useEffect(() => {
    document.addEventListener("mousedown", onClickOutSide);

    return () => {
      document.removeEventListener("mousedown", onClickOutSide);
    }
  }, [])

  // 컴포넌트 외부 클릭시 실행
  function onClickOutSide(e) {
    if (TopLevelRef.current !== e.target && !TopLevelRef.current.contains(e.target)) {
      SelectRef.current.style.border = `1px solid ${theme.line}`;
      SelectRef.current.style.height = `30px`;
      SelectRef.current.style.overflow = 'hidden';

      setTimeout(() => (
        setIsClicked(false)
      ), 150)
    }
  }

  // 옵션 호버링 시작 //
  function onMouseOver(index) {
    OptionRef.current[index] && (OptionRef.current[index].style.backgroundColor = theme.primary);
    OptionTextRef.current[index] && (OptionTextRef.current[index].style.color = theme.subBackground);
  }

  // 옵션 호버링 끝 //
  function onMouseLeave(index) {
    OptionRef.current[index] && (OptionRef.current[index].style.backgroundColor = theme.subBackground);
    OptionTextRef.current[index] && (OptionTextRef.current[index].style.color = theme.text);
  }

  // 선택박스 클릭시 //
  function onClick() {
    if (disabled) return;
    else if (!options[0]) return;

    if (isClicked) {
      SelectRef.current.style.border = `1px solid ${theme.line}`;
      SelectRef.current.style.height = `30px`;
      SelectRef.current.style.overflow = 'hidden';

      setTimeout(() => (
        setIsClicked(false)
      ), 150)
    }
    else {
      SelectRef.current.style.border = `1px solid ${theme.primary}`;
      SelectRef.current.style.height = `${32 * (displayOptions.length + 2)}px`;
      SelectRef.current.style.maxHeight = '160px';
      SelectRef.current.style.overflowY = 'scroll';
      SelectRef.current.style.overflowX = 'none';
      setIsClicked(true);
    }
  }

  // 옵션 클릭시 //
  function optionalClick(optionIndex) {
    setValue(displayOptions[optionIndex], index);
  }

  // 옵션 추가 클릭시 //
  function onClickAdditionOptionButton() {
    onClickAdditionOption();
  }

  return (
    <TopLevelWrapper
      ref={TopLevelRef}
      width={width}>
      <SelectBox
        ref={SelectRef}
        isClicked={isClicked}
        width={width}
        onClick={onClick}>
        <SelectedViewWrapper isClicked={isClicked} width={width}>
          <SelectedViewTextWrapper>
            <Text fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='pointer'>{getDisplayName(value.name)}</Text>
          </SelectedViewTextWrapper>
          <ArrowIcon $isClicked={isClicked} />
        </SelectedViewWrapper>
        {
          isClicked &&
          displayOptions.map((optionData, optionIndex) =>
            <OptionWrapper
              key={'PaymentOptions-' + optionIndex}
              ref={(element) => OptionRef.current[optionIndex] = element}
              onMouseOver={() => onMouseOver(optionIndex)}
              onMouseLeave={() => onMouseLeave(optionIndex)}
              onClick={() => optionalClick(optionIndex)}>
              <OptionTextWrapper>
                <Text ref={(element) => OptionTextRef.current[optionIndex] = element} fontFamily='NotoSansKR-Regular' fontSize={14} color={theme.text} cursor='pointer'>{getDisplayName(optionData.name)}</Text>
              </OptionTextWrapper>
              {
                optionData.id !== 0 &&
                <DeleteButton onClick={(e) => {
                  e.stopPropagation();
                  onClickDeleteOption(optionData.id);

                  SelectRef.current.style.border = `1px solid ${theme.line}`;
                  SelectRef.current.style.height = `30px`;
                  SelectRef.current.style.overflow = 'hidden';

                  setTimeout(() => (
                    setIsClicked(false)
                  ), 150)
                }}>
                  <DeleteIcon src={WhiteTrashIcon} />
                </DeleteButton>
              }
            </OptionWrapper>
          )
        }
        {
          isClicked &&
          <AdditionOptionWrapper
            isHover={isAdditionButtonHover}
            onMouseOver={() => setIsAddionButtonHover(true)}
            onMouseLeave={() => setIsAddionButtonHover(false)}
            onClick={onClickAdditionOptionButton}>
            <Text fontFamily='NotoSansKR-Medium' fontSize={12} color={isAdditionButtonHover ? theme.white : theme.text} cursor='pointer'>{t('paymentSelectBox.addPaymentOptionInSelect')}</Text>
          </AdditionOptionWrapper>
        }
      </SelectBox>
    </TopLevelWrapper>
  )
}

const TopLevelWrapper = styled.div`
	${props => !props.width && 'position: relative;'}
	display: flex;
	flex-direction: column;

	width: ${props => props.width ? `${props.width}px` : '100%'};
	height: 30px;
`;

const SelectBox = styled.div`
	position: absolute;
	display: flex;
	flex-direction: column;

	justify-content: flex-start;
	align-items: flex-start;

	width: ${props => props.width ? `${props.width}px` : '100%'};
	height: 30px;

	border-radius: 5px;
	overflow: hidden;

  border: 1px solid ${props => props.theme.line};
  box-sizing: border-box;
	background-color: ${props => props.theme.subBackground};

  transition: all 0.3s ease-in-out;

	&:hover {
		cursor: pointer;
	}

  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const SelectedViewWrapper = styled.div`
  display: flex;
  flex-direction: row;

	justify-content: space-between;
	align-items: center;

  padding: 3px 8px;

  width: ${props => props.width ? `${props.width}px` : '100%'};
  box-sizing: border-box;

  border-bottom: ${props => props.isClicked ? `1px solid ${props.theme.line}` : 'none'};
`;

const SelectedViewTextWrapper = styled.div`
  display: flex;
  flex-direction: row;

	justify-content: flex-start;
	align-items: flex-start;

  width: 100%;
  height: 20px;
  
  overflow: hidden;
`;

const ArrowIcon = styled(LeftArrowSvg)`
  width: 8px;
  height: auto;
  margin-right: 4px;
  flex-shrink: 0;
  transform: ${props => props.$isClicked ? 'rotate(90deg)' : 'rotate(-90deg)'};
  transition: transform 0.3s ease-in-out;

  & path {
    fill: ${props => props.theme.subText};
  }
`;

const OptionWrapper = styled.div`
	display: flex;
	flex-direction: row;

	justify-content: space-between;
	align-items: center;

  padding: 6px 8px;

	width: 100%;
  box-sizing: border-box;

  border-bottom: 1px solid ${props => props.theme.line};

	&:hover {
		cursor: pointer;
	}

  transition: all 0.3s ease-in-out;
`;

const OptionTextWrapper = styled.div`
	display: flex;
	flex-direction: row;

	justify-content: flex-start;
	align-items: flex-start;

	width: 80%;
  height: 20px;

  overflow: hidden;
`;

const AdditionOptionWrapper = styled.div`
	display: flex;
	flex-direction: column;

	justify-content: center;
	align-items: center;

  padding: 6px 8px;

	width: 100%;
  box-sizing: border-box;

  background-color: ${props => props.isHover ? props.theme.primary : props.theme.subBackground};

	&:hover {
		cursor: pointer;
	}
    
  transition: all 0.3s ease-in-out;
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 24px;
  height: 18px;

  border-radius: 2px;
  background-color: ${props => props.theme.pointRedAlpha50};

  &:hover {
    cursor: pointer;
    background-color: ${props => props.theme.pointRed};
  }

  transition: all 0.3s ease-in-out;
`;

const DeleteIcon = styled.img`
  width: 14px;
  height: 14px;
`;

const Text = styled.p`
  font-family: ${props => props.fontFamily};
  font-size: ${props => props.fontSize}px;

  color: ${props => props.color};

  margin: 0px;

  &:hover {
    cursor: ${props => props.cursor};
  }

  transition: all 0.3s ease-in-out;
`;